import {
  Accordion,
  Autocomplete,
  Checkbox,
  Dialog,
  LinearProgress,
  List,
  MenuItem,
  MenuList,
  Select,
  Switch,
  Tabs,
  TextField,
  styled,
} from "@mui/material";
import React, { forwardRef } from "react";
import { fontWeight, width } from "@mui/system";
import { DataGrid, GridPagination } from "@mui/x-data-grid";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { ControlpanelSecondaryButtonV2 } from "./StyledButton";
import { PropTypes } from 'prop-types';
import { SpinnerRing } from "./CustomSpinner";

export const NavbarLanguageMenuList = styled(MenuList)({
  padding: "0px",
  marginTop: "3px",
  borderRadius: "8px",
  background: "#F8FAFC",
  // border: "1px solid #E5E7EB",
  boxShadow:
    "0px 4px 3px 0px rgba(0, 0, 0, 0.10), 0px 10px 8px 0px rgba(0, 0, 0, 0.04)",
  ".MuiMenuItem-root": {
    padding: "10px 15px",
    "&:hover": {
      color: "#FFF",
      backgroundColor: "#3B82F6",
      borderRadius: "8px",
    },
  },
});

export const NavbarMenuItem = styled(MenuItem)({
  padding: "10px 20px",
  "&:hover": {
  },
});

export const CountryMenuList = styled(MenuList)({
  maxHeight: "240px",
  overflowY: "auto",
  ".MuiLink-root": {
    color: "#171717",
    textDecoration: "none",
  },
});

export const DiaLogForm = styled(Dialog)({
  ".MuiPaper-root": {
    width: "900px",
    borderRadius: "16px",
  },
});

const CustomDialogRoot = styled(Dialog)({
  ".MuiPaper-root": {
    width: "800px",
    borderRadius: "16px",
    position: "relative",
    overflow: "hidden",
  },
  "& .MuiTypography-root.MuiDialogTitle-root": {
    position: "relative",
    ":before": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: "1px",
      right: "1px",
      borderBottom: "1px solid #EFEFEF",
    },
  },
  "& .MuiDialogActions-root": {
    position: "relative",
    ":before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "1px",
      right: "1px",
      borderTop: "1px solid #EFEFEF",
    },
  },
  "& .MuiDialogTitle-root": {
    padding: "16px 24px 16px 24px",
  },
});

/**
 * Dialog chuẩn Control Panel.
 * @param {boolean} [loading] - đang submit: hiện spinner, chặn đóng (backdrop / Esc)
 */
export const CustomDialog = forwardRef(function CustomDialog(
  {
    loading = false,
    onClose,
    disableEscapeKeyDown = false,
    children,
    ...props
  },
  ref
) {
  return (
    <CustomDialogRoot
      ref={ref}
      {...props}
      onClose={loading ? undefined : onClose}
      disableEscapeKeyDown={loading || disableEscapeKeyDown}
    >
      {loading ? (
        <div className="absolute inset-0 z-[100] flex items-center justify-center bg-white/60 rounded-2xl pointer-events-auto">
          <SpinnerRing loading size={50} color="#3B82F6" />
        </div>
      ) : null}
      {children}
    </CustomDialogRoot>
  );
});

export const CustomPasswordLinearProgress = styled(LinearProgress)({
  marginTop: "5px",
  "&.MuiLinearProgress-root": {
    backgroundColor: "#E0E0E0",
  },
  "& .MuiLinearProgress-bar": {
    backgroundColor: "#3B82F6",
  },
});

export const CustomLabelLinearProgress = styled(LinearProgress)({
  height: "20px",
  borderRadius: "10px",
  "&.MuiLinearProgress-root": {
    backgroundColor: "#EFF6FF",
  },
  "& .MuiLinearProgress-bar": {
    backgroundColor: "#3B82F6",
  },
});

export const StyledDataGrid = styled(DataGrid)({
  border: "none",
  borderRadius: "8px",
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#F9FAFB",
    fontWeight: 700,

    "& .MuiDataGrid-columnHeaderTitle": {
      fontSize: 14,
      fontWeight: 700,
    },
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      fontWeight: 700,
    },
    "& .MuiDataGrid-columnHeaderTitleContainerContent": {
      fontWeight: 700,
    },
  },
  "& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainerContent": {
    fontWeight: 700,
  },
  "& .MuiDataGrid-main": {
    border: "1px solid #E2E8F0",
    borderRadius: "8px",
    overflow: "hidden",
  },
  "& .MuiDataGrid-columnHeaders": {
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px",
    fontWeight: 700,
  },
  "& .MuiDataGrid-cellContent": {
    fontSize: 14,
  },
  "&.MuiDataGrid-root .MuiDataGrid-cell:focus": {
    outline: "none",
  },
  "& .MuiDataGrid-cell:focus-within": {
    outline: "none",
  },
  "& .MuiInputBase-root.MuiOutlinedInput-root": {
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  "& .MuiDataGrid-row": {
    "&:hover": {
      cursor: "pointer",
    },
  },
  "& .MuiDataGrid-footerContainer": {
    border: "none",
    display: "block",
    paddingTop: "10px",
    "& .MuiInputBase-root": {
      height: "34px",

      input: {
        padding: "5px 5px 5px 20px",
      },

      "& .MuiSelect-select": {
        padding: "5.5px 34px 5.5px 24px",
      },
    },
    "& .MuiButtonBase-root": {
      margin: "0px",
      padding: "5px 5px",
    },
    "& .MuiDataGrid-selectedRowCount": {
      display: "none",
    },
  },
  // Ẩn mũi tên ↑ ↓
  "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
  {
    WebkitAppearance: "none",
    margin: 0,
  },
  "& input[type=number]": {
    textAlign: "center",
    MozAppearance: "textfield",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "5px !important",
    paddingTop: "7px !important",
    paddingBottom: "7px !important"
  },
});

export const CustomGridPagination = styled(GridPagination)({});

export const CustomTabs = styled(Tabs)({
  minHeight: "unset",

  "& .MuiTabs-scrollButtons": {
    display: "flex",
  },

  "& .MuiTabs-scrollButtons.Mui-disabled": {
    opacity: 0.3,
  },
  backgroundColor: "#F3F4F6",
  padding: "4px",
  borderRadius: "8px",

  "& .MuiTabs-flexContainer": {
    gap: "10px",
    display: "flex",
    justifyContent: "space-between",
    width: "100%",

    "& .MuiTab-root": {
      flexGrow: 1,
      color: "#1F2937",
      transition: "all 0.3s ease",

      "& .location-notification": {
        borderRadius: "999px",
        dislay: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "4px 8px",
        backgroundColor: "#DBEAFE",
        color: "#1F2937",
      },

      "&.Mui-selected": {
        backgroundColor: "#FFF",
        borderRadius: "8px",
        color: "#3B82F6",
        transition: "all 0.3s ease",

        "& .location-notification": {
          color: "#3B82F6",
        },
      },
    },
  },
});

export const CustomTabsV2 = styled(Tabs)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  padding: "2px",
  borderRadius: "8px",

  "& .MuiTabs-scroller": {
    [theme.breakpoints.down("sm")]: {
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
  },

  "& .MuiTabs-flexContainer": {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",

    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
      flexWrap: "nowrap",
    },
  },

  "& .MuiTab-root": {
    color: "#6B7280",
    fontWeight: 600,
    transition: "all 0.3s ease",
    borderBottom: "2px solid #F3F4F6",

    /* 🔴 QUAN TRỌNG */
    whiteSpace: "nowrap",

    /* Desktop */
    flexGrow: 1,

    /* 📱 Mobile */
    [theme.breakpoints.down("sm")]: {
      flexGrow: 0,          // ⬅️ bỏ ép đều
      minWidth: "max-content", // ⬅️ tab rộng theo chữ
      padding: "8px 16px",
    },

    "& .location-notification": {
      borderRadius: "999px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "4px 8px",
      backgroundColor: "#0057FF",
      color: "#1F2937",
    },

    "&.Mui-selected": {
      color: "#0057FF",
      borderBottom: "2px solid #0057FF",

      "& .location-notification": {
        color: "#0057FF",
      },
    },
  },
}));

export const CustomTabsMini = styled(Tabs)(({ theme }) => ({
  height: "40px",
  minHeight: "40px", // 👈 quan trọng

  "& .MuiTabs-scrollButtons": {
    display: "flex",
  },

  "& .MuiTabs-scrollButtons.Mui-disabled": {
    opacity: 0.3,
  },

  backgroundColor: "#F3F4F6",
  padding: "4px",
  borderRadius: "8px",

  "& .MuiTabs-flexContainer": {
    gap: "10px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center", // 👈 thêm cái này
    height: "100%",       // 👈 thêm

    "& .MuiTab-root": {
      flexGrow: 1,
      color: "#9CA3AF",
      transition: "all 0.3s ease",
      fontWeight: 600,

      minHeight: "32px", // 👈 fix Tab nhỏ lại
      height: "32px",    // 👈 đảm bảo không bị cao hơn
      padding: "0 12px", // 👈 tránh padding top-bottom quá lớn

      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      "&.Mui-selected": {
        backgroundColor: "#FFF",
        borderRadius: "8px",
        color: "#0057FF",

        "& .location-notification": {
          color: "#0057FF",
        },
      },
    },
  },
}));

export const CustomSecondaryButton = styled(ControlpanelSecondaryButtonV2)(() => ({
  minWidth: "40px",
  ".MuiSvgIcon-root": {
    fontSize: "20px",
  },
}));

// export const CustomTabs = styled(Tabs)({
//   backgroundColor: "#FFFFFF",
//   // padding: "4px",
//   borderRadius: "6px",

//   "& .MuiTabs-flexContainer": {
//     // gap: "10px",
//     display: "flex",
//     justifyContent: "space-between",
//     width: "100%",


//     "& .MuiTab-root": {
//       flexGrow: 1,
//       color: "#1F2937",
//       transition: "all 0.3s ease",
//       fontSize: '14px',

//       "& .location-notification": {
//         borderRadius: "999px",
//         dislay: "flex",
//         justifyContent: "center",
//         alignItems: "center",
//         padding: "4px 8px",
//         backgroundColor: "#DBEAFE",
//         color: "#1F2937",
//       },

//       "&.Mui-selected": {
//         backgroundColor: "#FFF",
//         borderBottom: '2px solid #3B82F6', 
//         // borderRadius: "6px",
//         color: "#3B82F6",
//         transition: "all 0.3s ease",

//         "& .location-notification": {
//           color: "#3B82F6",
//         },
//       },
//     },
//   },
// });

export const CustomAdvancedMenuList = styled(MenuList)({
  padding: "0px",
  "& .MuiMenuItem-root": {
    padding: "8px 10px",
  },
  "& .MuiLink-root": {
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    textDecoration: "none",
    color: "#1F2937",
    padding: "0px 12px",
  },
});

export const StyledDatePicker = styled(DatePicker)({
  width: "100%",

  "& .MuiInputBase-root ": {
    // color: '#6B7280',
    color: "#171717",
    fontWeight: 500,
    paddingRight: "12px",
    "&:hover fieldset": {
      borderColor: "#E5E7EB",
    },
  },
  "& .MuiInputBase-input.MuiOutlinedInput-input": {
    padding: "11.5px 0 11.5px 10.5px",
  },
  ".Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E5E7EB !important",
  },
  "& .MuiInputBase-input": {
    "&::placeholder": {
      opacity: 1,
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: "8px",
    border: "1px solid #E5E7EB",
  },
  ".MuiButtonBase-root": {
    padding: "9px",
    backgroundColor: "#D1D5DB",
    borderRadius: "0px 6px 6px 0px",
  },
});

export const IOSSwitch = styled(Switch)({
  width: 42,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#00E096",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#00E096",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: "#f5f5f5",
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.7,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: "#E9E9EA",
    opacity: 1,
    transition: {
      duration: 500,
    },
  },
});

export const NormalSwitch = styled(Switch)({
  width: 42,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#3B82F6",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#3B82F6",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: "#f5f5f5",
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.7,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: "#E9E9EA",
    opacity: 1,
    transition: {
      duration: 500,
    },
  },
});

export const CustomList = styled(List)({
  "& .MuiListItem-root": {
    padding: 0,
  },
});

export const StyledAutoComplete = styled(Autocomplete)({
  width: "100%",
  "& .MuiInputBase-root ": {
    minHeight: "44px",
    borderRadius: "10px",
    color: "#171717",
    fontWeight: 500,
    backgroundColor: "#fff",
    padding: "0px 12px 0px 6.5px",
    "& .MuiOutlinedInput-root": {
      padding: "10px",
    },
    "& fieldset": {
      border: "1px solid #E5E7EB",
    },
    "&:hover fieldset": {
      border: "1px solid #E5E7EB",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "1px solid #E5E7EB",
    },
    "&.Mui-focused fieldset": {
      border: "1px solid #E5E7EB", // border color when focused
    },
    ".MuiButtonBase-root": {
      color: "#FFF",
      padding: "13px 13px 13.5px 13px",
      backgroundColor: "#3B82F6",
      borderRadius: "0px 10px 10px 0px",
      width: "46px",
      ".MuiSvgIcon-root": {
        fontSize: "16px",
        color: "#fff",
      },
    },
    ".MuiButtonBase-root.MuiChip-root": {
      borderRadius: "10px",
      "&.MuiChip-root": {
        margin: "6px 5px 6px 0",
      },
      // '& .MuiSvgIcon-root': {
      //    // color: '#171717',
      // },
    },
  },
});

export const StyledTimePicker = styled(TimePicker)({
  width: "100%",

  "& .MuiInputBase-root ": {
    color: "#6B7280",
    fontWeight: 500,
    paddingRight: "0px",
    "&:hover fieldset": {
      borderColor: "#E5E7EB",
    },
  },
  "& .MuiInputBase-input.MuiOutlinedInput-input": {
    padding: "12px 0 12px 15px",
  },
  ".Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E5E7EB !important",
  },
  "& .MuiInputBase-input": {
    "&::placeholder": {
      opacity: 1,
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: "8px",
    border: "1px solid #E5E7EB",
  },
  ".MuiButtonBase-root": {
    padding: "14px",
    backgroundColor: "#D1D5DB",
    borderRadius: "0px 6px 6px 0px",
  },
});

export const StyledTextFieldTimePicker = styled(TextField)({
  width: "100%",

  "& .MuiInputBase-root ": {
    color: "#6B7280",
    fontWeight: 500,
    paddingRight: "0px",
    "&:hover fieldset": {
      borderColor: "#E5E7EB",
    },
  },
  "& .MuiInputBase-input.MuiOutlinedInput-input": {
    padding: "11.5px 0 11.5px 15px",
  },
  ".Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E5E7EB !important",
  },
  "& .MuiInputBase-input": {
    "&::placeholder": {
      opacity: 1,
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: "8px",
    border: "1px solid #E5E7EB",
  },
  ".MuiButtonBase-root": {
    padding: "13px 14px 13px 14px",
    backgroundColor: "#D1D5DB",
    borderRadius: "0px 6px 6px 0px",
  },
});

export const StyledBreaktimeAccordion = styled(Accordion)({
  "&.MuiAccordion-root.MuiPaper-root": {
    borderRadius: "8px",
    boxShadow: "none",
    border: "1px solid #e3e6f0",
    "& .MuiButtonBase-root.MuiAccordionSummary-root": {
      borderRadius: "8px 8px 0 0",
      color: "#FFF",
    },
  },
  ".MuiAccordionSummary-expandIconWrapper": {
    "&.Mui-expanded": {
      transform: "none",
    },
  },
  ".MuiAccordionActions-root": {
    padding: "0px 16px 16px 16px",
  },
});

export const StyledAdditionalOptionsAccordion = styled(Accordion)({
  "&.MuiAccordion-root.MuiPaper-root": {
    borderRadius: "8px",
    boxShadow: "none",
    "& .MuiButtonBase-root.MuiAccordionSummary-root": {
      borderRadius: "8px 8px 0 0",
      color: "#FFF",
    },
  },
  ".MuiAccordionSummary-expandIconWrapper": {
    "&.Mui-expanded": {
      transform: "none",
    },
  },
  ".MuiAccordionDetails-root": {
    paddingLeft: "0px",
    paddingRight: "0px",
  },
  ".MuiAccordionActions-root": {
    padding: "0px 16px 16px 16px",
  },
});

export const StyledSelect = styled(Select)({
  width: "100%",

  "&.MuiInputBase-root, & .MuiOutlinedInput-root": {
    height: 40,
    minHeight: 40,
    borderRadius: 6,
    border: "none",
    color: "#171717",
    fontWeight: 500,
    paddingLeft: 0,
    paddingRight: 0,
  },

  /* Viền */
  "& fieldset": {
    border: "1px solid #E5E7EB",
    transition: "0.3s",
  },
  "&:hover fieldset": {
    border: "1px solid #E5E7EB",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "1px solid #E5E7EB",
  },
  "&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": {
    border: "1px solid #3B82F6",
    boxShadow: "0 0 0 4px rgba(59,130,246,0.25)",
    transition: "0.3s",
  },

  "& .MuiSelect-select": {
    minHeight: 40,
    lineHeight: "40px",
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 12,
    paddingRight: 36,
    display: "flex",
    alignItems: "center",
  },

  "& .MuiSelect-select.MuiOutlinedInput-input": {
    paddingTop: 0,
    paddingBottom: 0,
  },
});

export const StyledCheckbox = styled(Checkbox)({
  "&.MuiButtonBase-root.MuiCheckbox-root": {
    borderRadius: "99px",
    color: "#E5E7EB",
    padding: 0,
    "&.Mui-checked": {
      color: "#1976d2",
    },
  },
});

export const CustomTabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <div className="mt-3 min-w-0 w-full max-w-full overflow-hidden">
          {children}
        </div>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};
